/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import { SystemEntity } from '@backstage/catalog-model';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { TabPanel } from '../../../../components/tabpanel/TabPanel.tsx';
import {
  Typography,
  CardContent,
  Card,
  Snackbar,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  withStyles,
  Chip,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { createStyles, Theme } from '@mui/material';
import { FeaturesListingMonitoringSubpageCard } from './components/FeaturesListingCardMonitoringSubpage.tsx';
import { FeatureFlag, WorkspaceData } from './types.ts';
import { FeatureFlagService } from '../../../../services/FeatureFlagService.ts';

export const FeaturesMonitoringSubpage = ({
  system,
}: {
  system: SystemEntity;
}) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const featureFlagService = new FeatureFlagService(fetchApi, discoveryApi);

  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);
  const [alert, setAlert] = useState<{
    message: string;
    severity: 'success' | 'error';
  } | null>(null);

  const StyledTableCell = withStyles((theme: Theme) =>
      createStyles({
        head: {
          backgroundColor: '#a0a0a0',
          color: theme.palette.common.white,
        },
        body: {
          fontSize: 14,
        },
        '@media (max-width: 768px)': {
          head: {
            fontSize: '12px',
          },
          body: {
            fontSize: '12px',
          },
        },
      }),
  )(TableCell);

  const [tabValue, setTabValue] = useState('DEV');
  const [envWorkspaces, setEnvWorkspaces] = useState<WorkspaceData[]>([]);

  const fetchFlagDetails = async (flagId: string) => {
    try {
      featureFlagService.getFeatureFlagDetails(flagId)
        .then(fullFlag => {
          setSelectedFlag(fullFlag);
        })
        .catch(err => {
          setAlert({
            message: `Error fetching flag details: ${err}`,
            severity: 'error',
          });
        });
    } catch (err) {
      setAlert({
        message: `Error fetching flag details: ${err}`,
        severity: 'error',
      });
    }
  };

  const searchFeatureFlags = async () => {
    featureFlagService.searchFeatureFlagsByState(system, 'ACTIVE')
        .then(res => {
          setFlags(res.items);
          if (res.items.length > 0) {
            fetchFlagDetails(res.items[0].id);
          }
        });
  };

  const processWorkspaces = (environment: string) => {
    const currentEnvironment = selectedFlag?.environments?.find(
      env => env.environment === environment,
    );
    setEnvWorkspaces(currentEnvironment ? currentEnvironment.data : []);
  };

  const handleTabChange = (newTabValue: string) => {
    setTabValue(newTabValue);
    processWorkspaces(newTabValue);
  };

  useEffect(() => {
    searchFeatureFlags();
  }, [discoveryApi, fetchApi, system]);

  useEffect(() => {
    if (selectedFlag) {
      processWorkspaces(tabValue);
    }
  }, [selectedFlag, tabValue]);

  return (
    <Box
      mt={2}
      display="flex"
      height={550}
      flexDirection="row"
      gridColumnGap={16}
    >
      <Card style={{ flexShrink: 0 }}>
        <CardContent>
          <FeaturesListingMonitoringSubpageCard
            selectedFlag={selectedFlag}
            setSelectedFlagId={fetchFlagDetails}
            featureFlags={flags}
          />
        </CardContent>
      </Card>

      <Card style={{ flexGrow: 1 }}>
        <CardContent>
          <Box display="flex" flexDirection="column" gridRowGap={24}>
            {selectedFlag ? (
              <>
                <Typography variant="h6">{selectedFlag.code}</Typography>
                {selectedFlag.tags && selectedFlag.tags.length > 0 && (
                  <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                    {selectedFlag.tags.map(tag => (
                      <Chip key={tag} label={tag} size="small" style={{ height: 20, fontSize: 11 }} />
                    ))}
                  </Box>
                )}
                <Typography variant="body1" gutterBottom>
                  {selectedFlag.description}
                </Typography>

                <Tabs
                  value={tabValue}
                  onChange={(_event, newTabValue) =>
                    handleTabChange(newTabValue)
                  }
                >
                  <Tab label="DEVELOPMENT" value="DEV" />
                  <Tab label="STAGING" value="STG" />
                  <Tab label="PRODUCTION" value="PRD" />
                </Tabs>
              </>
            ) : (
              <Typography variant="body1">
                Select a flag to view details
              </Typography>
            )}
            <TabPanel value={tabValue} index={tabValue}>
              <Box display="flex" flexDirection="column" gridRowGap={16}>
                <Box
                  height={350}
                  style={{ overflow: 'auto', scrollbarWidth: 'thin' }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell width={400}>
                          <b>Workspace</b>
                        </StyledTableCell>
                        <StyledTableCell width={400}>
                          <b>Cliente</b>
                        </StyledTableCell>
                        <StyledTableCell align="center" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {envWorkspaces.map(row => (
                        <TableRow key={row.workspace + tabValue}>
                          <TableCell component="th" scope="row">
                            {row.workspace}
                          </TableCell>
                          <TableCell>{row.account?.name}</TableCell>
                          <TableCell align="center">
                            <Box display="flex" alignItems="center">
                              <Chip
                                label={
                                  row.enabled
                                    ? `Enabled${
                                        row.updatedAt
                                          ? ` at: ${row.updatedAt}`
                                          : ''
                                      }`
                                    : `Disabled`
                                }
                                style={{
                                  backgroundColor: row.enabled
                                    ? '#4CAF50'
                                    : '#9E9E9E',
                                  color: '#fff',
                                  fontWeight: 'bold',
                                  margin: 0,
                                }}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </TabPanel>
          </Box>

          {alert && (
            <Snackbar
              open={!!alert}
              autoHideDuration={3000}
              onClose={() => setAlert(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
              <MuiAlert
                elevation={6}
                variant="filled"
                onClose={() => setAlert(null)}
                severity={alert.severity}
              >
                {alert.message}
              </MuiAlert>
            </Snackbar>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
